import "./browse-page.css";
import { Button, Col, Form, Row } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StorageAPIClient } from "../../api/storage-api-client";
import { FileBrowsingTable } from "../layout/file-browser/file-browser-table";
import {
  ContainerData,
  ContainerFolderStructure,
  ContainersList,
  RequestDataQuery,
} from "../../api/data-types";
import { PathArrayComponent } from "../layout/path-array/path-array";
import {
  getContainerData,
  getContainerList,
  limit_options,
  loadTheOnlyContainer,
  pathToArray,
  wasChangeSignificant,
} from "./util/browse-page-util";
import { MetadataPreview } from "../layout/metadata-preview/metadata-preview";
import { emptyRequestDataProperties as emptyData } from "../../api/data-types";
import { DropdownListSelection } from "../layout/list-selection-dropdown/list-selection-dropdown";
import {
  BrowsePageSearchParameters,
  PagingStack,
  PathArrayProps,
} from "./util/data-types/data-types-browse-page";
import { updateRequestDataLimit } from "../search-page/util/search-page-util";
import { UserBrowsingSessionProps } from "../../App";
import { File } from "../../api/data-types";
import { Overlay } from "./../overlay/overlay";
import { FolderOpenOutlined } from "@ant-design/icons";
import { FolderModal } from "../layout/folder-modal/folder-modal";

const DEFAULT_LIMIT = 100;
const DEFAULT_CONTAINER_LABEL = "";

export const BrowsePage = ({
  theme,
  containerState,
  updateContainerState,
}: UserBrowsingSessionProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [containerData, setContainerData] = useState<ContainersList>({
    containers: [],
  });

  const [selectedContainer, setSelectedContainer] = useState<ContainerData>(
    new ContainerData(DEFAULT_CONTAINER_LABEL, "", [])
  );

  const [containerWriteData, setContainerWriteData] = useState<ContainersList>({
    containers: [],
  });

  const [selectedLimit, setSelectedLimit] = useState<number>(DEFAULT_LIMIT);

  const [loadingComponent, setLoadingComponent] = useState<boolean>(false);

  const [form] = Form.useForm<FormData>();

  const [openFolderModal, setOpenFolderModal] = useState<boolean>(false);

  const currentUrlSearchParameters = new BrowsePageSearchParameters(
    emptyData.EMPTY_CONTAINER,
    emptyData.EMPTY_PARENT_FOLDER,
    selectedLimit.toString()
  );

  const [requestDataQuerry, setRequestDataQuerry] = useState<RequestDataQuery>(
    new RequestDataQuery(
      emptyData.EMPTY_FOLDERS,
      emptyData.EMPTY_FILES,
      emptyData.EMPTY_PATH,
      emptyData.EMPTY_CONTAINER,
      emptyData.EMPTY_PARENT_FOLDER,
      emptyData.EMPTY_CURRENT_TOKEN,
      emptyData.EMPTY_NEXT_TOKEN,
      emptyData.EMPTY_PREVIOUS_TOKEN,
      selectedLimit,
      false
    )
  );

  const [pathArray, setPathArray] = useState<PathArrayProps>({
    pathElements: [],
    path: "",
  });

  const [isFileSelected, setIsFileSelected] = useState<boolean>(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const [folderPath, setFolderPath] = useState<string>("");

  const [currentPagingStack, setCurrentPagingStack] = useState<PagingStack>(
    new PagingStack([])
  );

  const [containerWriteAccess, setContainerWriteAccess] =
    useState<boolean>(true);

  const [forceReload, setForceReload] = useState<boolean>(false);

  const updateContainer = async (newContainer: string) => {
    const container = await getContainerData(newContainer);
    setSelectedContainer(container);
    updateContainerState(newContainer);
    return container;
  };

  const updateComponentsDataQuerry = async (
    aContainer: ContainerData,
    aContinuationToken: string,
    aPreviousToken: string,
    aLimit: number,
    aFolder?: string
  ) => {
    setLoadingComponent(true);
    var newDataQuerry: Promise<RequestDataQuery>;
    if (aFolder) {
      newDataQuerry = StorageAPIClient.singleDataResponseForContainerContents(
        aContainer,
        aContinuationToken,
        aPreviousToken,
        aLimit,
        aFolder
      );
    } else {
      newDataQuerry = StorageAPIClient.singleDataResponseForContainerContents(
        aContainer,
        aContinuationToken,
        aPreviousToken,
        aLimit
      );
    }
    setRequestDataQuerry(await newDataQuerry);
    const tempPath = (await newDataQuerry).path;
    setPathArray({ pathElements: pathToArray(tempPath), path: tempPath });
    setIsFileSelected(false);
    setSelectedRowKeys([]);
    setLoadingComponent(false);
  };

  const handleContainersDropdownMenuClick = async (containerName: string) => {
    const container = await updateContainer(containerName);
    setSelectedContainer(await getContainerData(containerName));
    if (
      container.containerName !== "" &&
      container.containerName !== DEFAULT_CONTAINER_LABEL
    ) {
      updateComponentsDataQuerry(
        container,
        emptyData.EMPTY_CURRENT_TOKEN,
        emptyData.EMPTY_PREVIOUS_TOKEN,
        selectedLimit
      );
    }
    setCurrentPagingStack(new PagingStack([]));
  };

  const handleFolderClick = async (folderName: string) => {
    updateComponentsDataQuerry(
      selectedContainer,
      emptyData.EMPTY_CURRENT_TOKEN,
      emptyData.EMPTY_PREVIOUS_TOKEN,
      selectedLimit,
      folderName
    );
    setCurrentPagingStack(new PagingStack([]));
  };

  const handlePathArrayClick = async (
    pathElements: string[],
    index: number
  ) => {
    if (index === 0) {
      updateComponentsDataQuerry(
        selectedContainer,
        emptyData.EMPTY_CURRENT_TOKEN,
        emptyData.EMPTY_PREVIOUS_TOKEN,
        selectedLimit
      );
    } else {
      const folder = pathElements.slice(1, index + 1).join("/");
      updateComponentsDataQuerry(
        selectedContainer,
        emptyData.EMPTY_CURRENT_TOKEN,
        emptyData.EMPTY_PREVIOUS_TOKEN,
        selectedLimit,
        folder
      );
    }
    setCurrentPagingStack(new PagingStack([]));
  };

  const updateFileBrowserPagingLimit = async (limit: number) => {
    setSelectedLimit(limit);
    if (selectedContainer.containerName !== DEFAULT_CONTAINER_LABEL) {
      updateComponentsDataQuerry(
        selectedContainer,
        emptyData.EMPTY_CURRENT_TOKEN,
        emptyData.EMPTY_PREVIOUS_TOKEN,
        limit,
        requestDataQuerry.parentFolder
      );
    } else {
      setRequestDataQuerry((prevData) =>
        updateRequestDataLimit(prevData, limit)
      );
    }
    setCurrentPagingStack(new PagingStack([]));
  };

  const navigateUsingContinuationToken = async (token: string) => {
    if (token === requestDataQuerry.nextContinuationToken) {
      await updateComponentsDataQuerry(
        selectedContainer,
        token,
        requestDataQuerry.currentContinuationToken,
        requestDataQuerry.limit,
        requestDataQuerry.parentFolder
      ).then(() =>
        currentPagingStack.previousTokens.push(
          requestDataQuerry.previousContinuationToken
        )
      );
    }
    if (token === requestDataQuerry.previousContinuationToken) {
      await updateComponentsDataQuerry(
        selectedContainer,
        token,
        currentPagingStack.previousTokens[
        currentPagingStack.previousTokens.length - 1
        ],
        requestDataQuerry.limit,
        requestDataQuerry.parentFolder
      ).then(() => currentPagingStack.previousTokens.pop());
    }
  };

  const loadBrowseSessionFromUrl = async (containersList: string[]) => {
    setLoadingComponent(true);
    if (containersList.includes(currentUrlSearchParameters.container)) {
      const limit =
        currentUrlSearchParameters.limit !== selectedLimit.toString() &&
          !isNaN(Number(currentUrlSearchParameters.limit))
          ? Number(currentUrlSearchParameters.limit)
          : selectedLimit;
      setSelectedLimit(limit);
      updateContainer(currentUrlSearchParameters.container);
      setSelectedContainer((prevData) => ({
        ...prevData,
        name: currentUrlSearchParameters.container,
      }));
      const newContainer: ContainerData = {
        containerName: currentUrlSearchParameters.container,
        folderStructure: new ContainerFolderStructure(""),
        metadataTemplate: [],
      };
      if (
        newContainer.containerName !== "" &&
        newContainer.containerName !== DEFAULT_CONTAINER_LABEL
      ) {
        await updateComponentsDataQuerry(
          newContainer,
          emptyData.EMPTY_CURRENT_TOKEN,
          emptyData.EMPTY_PREVIOUS_TOKEN,
          limit,
          currentUrlSearchParameters.folder
        );
      }
    } else {
      currentUrlSearchParameters.container = containerState
        ? containerState
        : containersList[0];
      updateContainer(currentUrlSearchParameters.container);
      await loadTheOnlyContainer(
        currentUrlSearchParameters.container,
        handleContainersDropdownMenuClick
      );
    }
    setLoadingComponent(false);
  };

  const isUrlValid = (searchParams: URLSearchParams) => {
    if (searchParams.toString() === "") {
      return false;
    }
    if (
      !searchParams.get("container") ||
      searchParams.get("container") === (DEFAULT_CONTAINER_LABEL || "")
    ) {
      return false;
    }
    return true;
  };

  const navigateWithSearchParams = (
    oldSearchParams: URLSearchParams,
    searchParams: URLSearchParams
  ) => {
    const oldSearch = oldSearchParams.toString();
    const newSearch = searchParams.toString();
    if (
      isUrlValid(oldSearchParams) &&
      oldSearch !== newSearch &&
      wasChangeSignificant(oldSearchParams, searchParams)
    ) {
      navigate({
        pathname: "",
        search: newSearch,
      });
    } else {
      navigate(
        {
          pathname: "",
          search: newSearch,
        },
        { replace: true }
      );
    }
  };

  const closePreview = async () => {
    await updateComponentsDataQuerry(
      selectedContainer,
      requestDataQuerry.currentContinuationToken,
      requestDataQuerry.previousContinuationToken,
      requestDataQuerry.limit,
      requestDataQuerry.parentFolder
    );
    setIsFileSelected(false);
    setSelectedRowKeys([]);
    setSelectedFiles([]);
  };

  useEffect(() => {
    let urlParams = new URLSearchParams(window.location.search);
    if (urlParams.toString() === "") {
      setForceReload(!forceReload);
    }
  }, [window.location.search]);

  useEffect(() => {
    if (
      containerWriteData.containers
        .map((container) => container.containerName)
        .includes(selectedContainer.containerName)
    ) {
      setContainerWriteAccess(true);
    } else {
      setContainerWriteAccess(false);
    }
  }, [selectedContainer, containerWriteData]);

  useEffect(() => {
    let files: File[] = [];
    for (let i = 0; i < selectedRowKeys.length; i++) {
      if (!isNaN(Number(selectedRowKeys[i]))) {
        files = [...files, requestDataQuerry.files[Number(selectedRowKeys[i])]];
      }
    }
    setSelectedFiles(files);
    if (files.length > 0) {
      setIsFileSelected(true);
    } else {
      setIsFileSelected(false);
    }
  }, [selectedRowKeys]);

  const firstRenderForUseEffect = useRef(true);
  const firstRenderForPopstate = useRef(true);
  const popstateHistoryBlocker = useRef(false);
  useEffect(() => {
    if (firstRenderForUseEffect.current) {
      firstRenderForUseEffect.current = false;
    } else {
      let oldParams = searchParams.toString();
      searchParams.set(
        "container",
        selectedContainer.containerName === DEFAULT_CONTAINER_LABEL
          ? ""
          : requestDataQuerry.containerName
      );
      searchParams.set("folder", requestDataQuerry.parentFolder);
      searchParams.set("limit", requestDataQuerry.limit.toString());
      currentUrlSearchParameters.updateSearchProperties(searchParams);
      if (!popstateHistoryBlocker.current) {
        navigateWithSearchParams(new URLSearchParams(oldParams), searchParams);
      } else {
        popstateHistoryBlocker.current = false;
      }
      firstRenderForPopstate.current = false;
    }
  }, [requestDataQuerry]);

  //on component mounting do:
  useEffect(() => {
    getContainerList("Read")
      .then((containersList) => {
        const containersData: ContainerData[] = containersList.map((name) => ({
          containerName: name,
          folderStructure: new ContainerFolderStructure(""),
          metadataTemplate: [],
        }));

        setContainerData((prevData) => ({
          ...prevData,
          containers: containersData,
        }));
        return containersList;
      })
      .then((containersList) => {
        currentUrlSearchParameters.updateSearchProperties(searchParams);
        loadBrowseSessionFromUrl(containersList)
          .then(() => {
            let container = currentUrlSearchParameters.container;
            //localStorage has priority over first container from the list, overriden if null
            return container;
          })
          .then((container) => {
            getContainerList("Write")
              .then((containersWriteList) => {
                const containerWritesData: ContainerData[] =
                  containersWriteList.map((name) => ({
                    containerName: name,
                    folderStructure: new ContainerFolderStructure(""),
                    metadataTemplate: [],
                  }));
                setContainerWriteData((prevData) => ({
                  ...prevData,
                  containers: containerWritesData,
                }));
                return containersWriteList;
              })
              .then((containersWriteList) => {
                if (containersWriteList.indexOf(container) !== -1) {
                  setContainerWriteAccess(true);
                } else {
                  setContainerWriteAccess(false);
                }
              });
          });
      })
      .catch((error) => {
        console.error("Error on mount:", error.message);
      });
  }, [forceReload]);

  const updateFolderValue = (aPath: string, updatePath: boolean) => {
    setOpenFolderModal(false);
    if (updatePath) {
      return updateComponentsDataQuerry(
        selectedContainer,
        emptyData.EMPTY_CURRENT_TOKEN,
        emptyData.EMPTY_PREVIOUS_TOKEN,
        selectedLimit,
        aPath
      );
    }
  };

  useEffect(() => {
    form.setFieldValue("folder", folderPath);
  }, [folderPath]);

  window.onpopstate = async function handleNavigation() {
    const url = new URL(window.location.href);
    if (!firstRenderForPopstate.current) {
      const urlParams = new URLSearchParams(url.searchParams);
      currentUrlSearchParameters.updateSearchProperties(urlParams);
      loadBrowseSessionFromUrl(
        containerData.containers.map((cD) => cD.containerName)
      ).then(() => {
        popstateHistoryBlocker.current = true;
      });
      firstRenderForPopstate.current = false;
    } else {
      const urlParams = new URLSearchParams(url.searchParams);
      currentUrlSearchParameters.updateSearchProperties(urlParams);
      popstateHistoryBlocker.current = true;
      firstRenderForPopstate.current = false;
    }
  };

  return (
    <div className="browsePageDiv">
      {containerData.containers.length === 0 && (
        <div className="noAccess">
        <Overlay 
          noAccesText="You don't have permissions to any containers."
        />
        </div>
      )}
      {loadingComponent && (
        <Overlay noAccesText="Loading..." />
      )}
      <Row className="gsk-sub-header-menu">
        <Col className="containerText">Container :</Col>
        <Col className="containerDropdown">
          <DropdownListSelection
            theme={theme}
            optionsList={containerData.containers.map(
              (containerData) => containerData.containerName
            )}
            onMenuClick={handleContainersDropdownMenuClick}
            placeholderText={selectedContainer.containerName}
          />
        </Col>
        <Col flex={1} className="path">
          <PathArrayComponent
            theme={theme}
            pathElements={pathArray.pathElements}
            onPathArrayClick={handlePathArrayClick}
            numberOfDisabled={0}
          />
        </Col>
        <Col>
          <Button
            type="default"
            className="jumpToButton"
            icon={<FolderOpenOutlined />}
            onClick={() => {
              setOpenFolderModal(true);
            }}
            disabled={selectedContainer.containerName === ""}
          >
            Open Folder
          </Button>
          <FolderModal
            theme={theme}
            isOpen={openFolderModal}
            containerName={selectedContainer.containerName}
            rootFolderPath={folderPath}
            sendPathToParent={updateFolderValue}
            showInput={false}
            numberOfDisabledInPath={0}
          ></FolderModal>
        </Col>
        <Col className="pageLimit">Select page limit :</Col>
        <Col className="pageDropdown">
          <DropdownListSelection
            theme={theme}
            optionsList={limit_options.map(String)}
            onMenuClick={(label) => updateFileBrowserPagingLimit(Number(label))}
            placeholderText={String(selectedLimit)}
          />
        </Col>
      </Row>
      <Row className="browser">
        {selectedContainer.containerName !== DEFAULT_CONTAINER_LABEL && (
          <Col span={isFileSelected ? 18 : 24}>
            <FileBrowsingTable
              selectedSearchType="none"
              theme={theme}
              filterOn={false}
              dataQuerry={requestDataQuerry}
              onFolderClick={handleFolderClick}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={(keys: React.Key[]) =>
                setSelectedRowKeys(keys)
              }
              showFolderColumn={false}
              showSelectFolderAction={false}
              onSelectFolderClick={() => {}}
              continuationTokenNavigation={navigateUsingContinuationToken}
              previousTokens={currentPagingStack.previousTokens}
              ySizeVH={"calc(100vh - 250px)"}
              sendFilterAndSorting={() => {}}
              filterFieldsFromParent={[]}
              sizesWithUnitFromParent={[]}
            />
          </Col>
        )}
        {selectedContainer.containerName === DEFAULT_CONTAINER_LABEL && (
          <Col span={24}>
            <p className="containerSelection">Select a container</p>
          </Col>
        )}
        {isFileSelected && (
          <>
            <Col span={6}>
              <MetadataPreview
                theme={theme}
                selectedContainer={selectedContainer}
                selectedFiles={selectedFiles}
                closePreview={closePreview}
                containerAccess={containerWriteAccess}
              />
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};
